/**
 * MapLife統合用の高度なアップロード機能
 * 既存のregistPicture関数を拡張してHEIC対応とプログレス表示を追加
 */

// 動的import方式でモジュールを読み込み
let AdvancedImageUploader = null;
let ProgressBarManager = null;

// 初期化時にモジュールを読み込み
async function loadModules() {
  try {
    if (window.AdvancedImageUploader) {
      AdvancedImageUploader = window.AdvancedImageUploader;
    }
    if (window.progressBarManager) {
      ProgressBarManager = window.progressBarManager;
    }
  } catch (error) {
    console.error('Enhanced upload modules loading error:', error);
  }
}

/**
 * 既存のregistPicture関数を拡張
 */
async function enhancedRegistPicture(params) {
  // すでに処理中のフラグを確認（二重実行・無限ループ防止）
  if (params._enhanced_processing) {
    if (window.originalRegistPicture && window.originalRegistPicture !== enhancedRegistPicture) {
      return window.originalRegistPicture(params);
    }
    return;
  }
  params._enhanced_processing = true;

  const { file, ...others } = params;
  // 単一ファイルのアップロード
  if (file) {
    return handleSingleFileUpload(file, params);
  } else {
    // 元の関数にフォールバック
    if (window.originalRegistPicture && window.originalRegistPicture !== enhancedRegistPicture) {
      return window.originalRegistPicture(params);
    }
    return null;
  }
}

/**
 * 単一ファイルのアップロード処理
 */
async function handleSingleFileUpload(file, params) {
  const uploadId = `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  try {
    // モジュールが読み込まれていない場合は読み込み
    if (!AdvancedImageUploader || !ProgressBarManager) {
      await loadModules();
    }

    // Progress bar display disabled
    
    // 高度なアップローダーを初期化
    const uploader = new window.AdvancedImageUploader({
      maxWidth: 1920,
      maxHeight: 1080,
      quality: 0.9,
      minQuality: 0.75,
      targetMaxBytes: 1.5 * 1024 * 1024,
      variantQuality: 0.88,
      thumbnailQuality: 0.8,
      enableWebP: false, // MapLifeでは互換性のためJPEGを使用
      enableHEIC: true,
      progressCallback: () => {}
    });

    // アップロード実行
    const results = await uploader.uploadFiles([file], {
      app_session_id: params.app_session_id || window.Cookies?.get?.('session') || '',
      checkSpot: params.checkSpot || 'default',
      ...params.uploadOptions
    });

    // 成功時の処理
    const result = results[0];
    if (result && result.success) {
      // 従来のsuccessコールバックを実行
      if (params.success && typeof params.success === 'function') {
        params.success(result);
      }

      return result;
    } else {
      throw new Error(result?.error || 'アップロードに失敗しました');
    }

  } catch (error) {
    console.error('Enhanced upload error:', error);
    
    // HEICファイルの場合、またはプログレスバー表示中の場合はフォールバックしない
    const isHEIC = file.name.toLowerCase().match(/\.(heic|heif)$/);
    
    if (isHEIC) {
      // HEICは元々のregistPictureでは扱えないため、ここでエラー終了
      if (params.error && typeof params.error === 'function') {
        params.error(error);
      }
      return { success: false, error: error.message };
    }

    // 元の関数にフォールバック（重複実行を避けるためコールバックは呼び出さない）
    if (window.originalRegistPicture && 
        window.originalRegistPicture !== enhancedRegistPicture && 
        window.originalRegistPicture !== window.registPicture &&
        !window.originalRegistPicture._isMapLifeEnhanced) {
      console.warn('拡張アップロードに失敗したため、標準アップロードでのフォールバックを試みます');
      // 進捗表示を消去してからフォールバック
      setTimeout(() => window.progressBarManager?.remove(uploadId), 2000);
      return window.originalRegistPicture(params);
    }

    // どちらも利用できない場合はエラーを通知
    if (params.error && typeof params.error === 'function') {
      params.error(error);
    }
    throw error;
  }
}

/**
 * 複数ファイルの一括アップロード
 */
async function bulkUploadFiles(files, options = {}) {
  const uploadId = `bulk_${Date.now()}`;
  
  try {
    // モジュールが読み込まれていない場合は読み込み
    if (!window.AdvancedImageUploader || !window.progressBarManager) {
      await loadModules();
    }

    // Progress bar display disabled
    
    // 高度なアップローダーを初期化
    const uploader = new window.AdvancedImageUploader({
      maxWidth: 1920,
      maxHeight: 1080,
      quality: 0.9,
      minQuality: 0.75,
      targetMaxBytes: 1.5 * 1024 * 1024,
      variantQuality: 0.88,
      thumbnailQuality: 0.8,
      enableWebP: false,
      enableHEIC: true,
      concurrentUploads: 1, // 直列実行に変更してサーバー側の競合を回避
      progressCallback: () => {}
    });

    // アップロード実行
    const results = await uploader.uploadFiles(files, {
      app_session_id: options.app_session_id || window.Cookies?.get?.('session') || '',
      checkSpot: options.checkSpot || 'default',
      ...options
    });

    // 成功/失敗の統計を確認
    const successCount = results.filter(r => r && r.success).length;
    const failCount = results.length - successCount;

    return results;

  } catch (error) {
    console.error('Bulk upload error:', error);
    
    throw error;
  }
}

/**
 * HEIC変換のみ実行（アップロードなし）
 */
async function convertHEICFiles(files) {
  if (!window.heicSupport) {
    console.warn('heicSupport is not available');
    return files;
  }

  const results = [];
  for (const file of files) {
    if (window.heicSupport.isHEICFile(file)) {
      try {
        console.log('HEIC変換を実行中:', file.name);
        const converted = await window.heicSupport.convertToJPEGWithFallback(file, {
          quality: 0.8,
          maxWidth: 1920,
          maxHeight: 1080
        });
        results.push(converted);
      } catch (error) {
        console.error('HEIC変換エラー:', error, file.name);
        results.push(file); // 変換失敗時は元ファイルを返す
      }
    } else {
      results.push(file);
    }
  }
  
  return results;
}

/**
 * ファイル入力からの入力を処理
 */
async function handleEnhancedFileInput(files, inputElement) {
  try {
    await bulkUploadFiles(files);
    // 成功したら入力をリセット
    if (inputElement) {
      inputElement.value = '';
    }
  } catch (error) {
    console.error('Enhanced file input handling error:', error);
  }
}
window.handleEnhancedFileInput = handleEnhancedFileInput;

function setupEnhancedUploadHandlers() {
  // 既存のファイル入力要素に拡張機能を追加
  $(document).on('change', 'input[type="file"][accept*="image"]', function(e) {
    const files = Array.from(e.target.files);
    
    if (files.length === 0) return;

    // HEIC/複数ファイル対応の確認
    const hasHEIC = files.some(file => 
      file.name.toLowerCase().includes('.heic') || 
      file.name.toLowerCase().includes('.heif')
    );

    // HEICファイルまたは複数ファイルの場合のみ拡張アップローダーを使用
    if (hasHEIC || files.length > 1) {
      // 拡張アップローダーを使用
      e.preventDefault();
      e.stopPropagation(); // 既存の処理を止める
      window.handleEnhancedFileInput(files, e.target);
      return; // 明示的にreturnして既存の処理を完全にブロック
    }
    // 単一の通常画像ファイル（JPEG等）の場合は何もせず、既存の処理を継続させる
  });
}

// 初期化
// 関数に識別フラグを付与
enhancedRegistPicture._isMapLifeEnhanced = true;

// 実行前に即座に退避を試みる（readyを待たずに可能な限り早く）
if (window.registPicture && !window.registPicture._isMapLifeEnhanced) {
  window.originalRegistPicture = window.registPicture;
  console.log('Original registPicture captured immediately');
}

$(document).ready(async () => {
  // モジュールを読み込み
  await loadModules();
  
  // 既存のregistPicture関数を退避（自身でないことを再度確認）
  if (window.registPicture && !window.registPicture._isMapLifeEnhanced && window.registPicture !== enhancedRegistPicture) {
    window.originalRegistPicture = window.registPicture;
    console.log('Original registPicture captured on ready');
  }

  // setupEnhancedUploadHandlers();
  
  // 既存のregistPicture関数を拡張版に置き換え
  window.registPicture = enhancedRegistPicture;
  window.enhancedRegistPicture = enhancedRegistPicture;
  
  console.log('Enhanced upload functionality initialized');
});

// グローバル関数として公開
window.enhancedRegistPicture = enhancedRegistPicture;
window.bulkUploadFiles = bulkUploadFiles;
window.convertHEICFiles = convertHEICFiles;

export {
  enhancedRegistPicture,
  bulkUploadFiles,
  convertHEICFiles
};
